import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarIcon, CalendarPlusIcon, CheckCircle2Icon, ClockIcon, MapPinIcon, RadioIcon, Share2Icon, UsersIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { Button } from '../components/ui/Button';
import { CtaBand } from '../components/ui/CtaBand';
import { NotFound } from './NotFound';
import { events, getEvent } from '../data/events';
export function EventDetail() {
  const {
    slug = ''
  } = useParams();
  const event = getEvent(slug);
  const [rsvp, setRsvp] = useState<'idle' | 'loading' | 'done'>('idle');
  if (!event) {
    return <NotFound data-id="element-750" data-mp="11vfcibt0dib5" />;
  }
  const others = events.filter(item => item.slug !== event.slug && !item.past).slice(0, 3);
  return <>
      <PageHero eyebrow={event.type} title={event.title} intro={event.description} crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Events',
      to: '/events'
    }, {
      label: event.city
    }]} image={event.image} imageAlt="" meta={<ul className="flex flex-wrap gap-x-6 gap-y-2 text-[15px] text-white/75" data-id="element-752" data-mp="1wvxwkxhm6a4q">
            <li className="flex items-center gap-2" data-id="element-753" data-mp="2aong8v9o72yo">
              <CalendarIcon aria-hidden className="h-4 w-4 text-gold-400" data-id="element-754" data-mp="2aqi0s8h14t28" />
              {event.date}
            </li>
            <li className="flex items-center gap-2" data-id="element-755" data-mp="1ix7z3gqvu8fu">
              <ClockIcon aria-hidden className="h-4 w-4 text-gold-400" data-id="element-756" data-mp="1ixrfgt0vworr" />
              {event.time}
            </li>
            <li className="flex items-center gap-2" data-id="element-757" data-mp="1dzzfrctzzyd0">
              <MapPinIcon aria-hidden className="h-4 w-4 text-gold-400" data-id="element-758" data-mp="1ry61xvpl49wu" />
              {event.city}, {event.state}
            </li>
          </ul>} data-id="element-751" data-mp="2w6ls38ha6qje" />

      <section className="bg-sand-50 py-14 lg:py-20" aria-label="Event details" data-id="element-759" data-mp="1n15vvksqj75q">
        <div className="mx-auto grid max-w-content gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-10" data-id="element-760" data-mp="33f5u8rbxmoqv">
          <div className="lg:col-span-7" data-id="element-761" data-mp="2yg2nn2gb0ags">
            <h2 className="font-display text-[26px] text-ink" data-id="element-762" data-mp="1w13hmsxryr82">About this event</h2>
            <p className="mt-4 text-[17px] leading-relaxed text-slate-700" data-id="element-763" data-mp="20zn6p27o0j4n">{event.description}</p>

            <h3 className="mt-10 text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-764" data-mp="2518l4rzul5gy">
              Speakers and participants
            </h3>
            <ul className="mt-4 divide-y divide-sand-300 border-y border-sand-300" data-id="element-765" data-mp="29ztfiasf4uxh">
              {event.speakers.map(speaker => <li key={speaker} className="flex items-center gap-3 py-4 text-[16px] text-slate-700" data-id="element-766" data-mp="1hbns1g9zcrty">
                  <UsersIcon aria-hidden className="h-4 w-4 text-green-600" data-id="element-767" data-mp="1maqv4v6egnza" />
                  {speaker}
                </li>)}
            </ul>

            <h3 className="mt-10 text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-768" data-mp="1r320wentn3zr">
              Getting there
            </h3>
            <div className="mt-4 overflow-hidden rounded-media border border-sand-200" data-id="element-769" data-mp="ykwo4u8rhtmy">
              <div className="flex h-48 items-center justify-center bg-sand-200" role="img" aria-label={`Map location for ${event.venue}, ${event.address}`} data-id="element-770" data-mp="r1nb8r11rct5">
                <span className="flex items-center gap-2 text-[14px] font-medium text-slate-600" data-id="element-771" data-mp="h2y2pov1j4pk">
                  <MapPinIcon aria-hidden className="h-5 w-5 text-green-700" data-id="element-772" data-mp="d2fbnx0z22of" />
                  {event.venue}
                </span>
              </div>
              <p className="bg-white px-5 py-4 text-[15px] text-slate-700" data-id="element-773" data-mp="84vxex49xpwh">{event.address}</p>
            </div>

            {event.past && <div className="mt-10 rounded-media border border-sand-200 bg-white p-6" data-id="element-774" data-mp="3uhui16erdq65">
                <h3 className="font-display text-[20px] text-ink" data-id="element-775" data-mp="3pjkm2tl7mljh">After the event</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-776" data-mp="3lhpkdnzda656">
                  The full replay, photo gallery and the text of the candidate’s remarks are
                  available in the newsroom.
                </p>
                <Link to="/news" className="mt-4 inline-flex items-center gap-2 text-[15px] font-semibold text-green-700" data-id="element-777" data-mp="1kgizqul4aa5y">
                  Watch the replay and read the remarks
                </Link>
              </div>}
          </div>

          <aside className="lg:col-span-5" data-id="element-778" data-mp="1fnpwkb5ar9vz">
            <div className="rounded-media border border-sand-200 bg-white p-7 lg:sticky lg:top-28" data-id="element-779" data-mp="1fmerm8047w3a">
              <dl className="space-y-4 text-[15px]" data-id="element-780" data-mp="10ttqkiwm6tbx">
                {[['Date', event.date], ['Time', event.time], ['Venue', `${event.venue}, ${event.address}`], ['Capacity', event.capacity]].map(([label, value]) => <div key={label} data-id="element-781" data-mp="2m4ts9sla0zkh">
                    <dt className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500" data-id="element-782" data-mp="2m5v4w1hxxu4b">
                      {label}
                    </dt>
                    <dd className="mt-1 text-slate-700" data-id="element-783" data-mp="317ux1p8k5dxf">{value}</dd>
                  </div>)}
              </dl>

              {event.livestream && <p className="mt-5 flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-[14px] font-medium text-green-800" data-id="element-784" data-mp="2wabgujsaur2u">
                  <RadioIcon aria-hidden className="h-4 w-4" data-id="element-785" data-mp="359ghbvnf8uhn" />
                  Streaming live on the campaign website
                </p>}

              {event.past ? <Link to="/events" className="mt-6 flex h-[52px] w-full items-center justify-center rounded-lg border border-ink/20 text-[15px] font-semibold text-ink transition-colors duration-150 ease-emphasis hover:border-ink/40 hover:bg-ink/[0.04]" data-id="element-786" data-mp="3k05w1803crq9">
                  See upcoming events
                </Link> : rsvp === 'done' ? <div role="status" className="mt-6 flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4" data-id="element-787" data-mp="1ymt9ndeadx0y">
                  <CheckCircle2Icon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-green-700" data-id="element-788" data-mp="3tq98hf9eqkmw" />
                  <div data-id="element-789" data-mp="2jy2d4pjitop">
                    <p className="text-[15px] font-semibold text-ink" data-id="element-790" data-mp="7gyb5ndryaji">You are registered.</p>
                    <p className="mt-1 text-[14px] leading-relaxed text-slate-600" data-id="element-791" data-mp="cg1dv16a84ob">
                      A confirmation with your entry reference has been sent. Please arrive thirty
                      minutes early for accreditation.
                    </p>
                  </div>
                </div> : <Button size="lg" className="mt-6 w-full" loading={rsvp === 'loading'} onClick={() => {
              setRsvp('loading');
              window.setTimeout(() => setRsvp('done'), 800);
            }} data-id="element-792" data-mp="q6wig19qzzm3">
                  {rsvp === 'loading' ? 'Registering…' : 'RSVP for this event'}
                </Button>}

              <div className="mt-4 flex gap-3" data-id="element-793" data-mp="2oqkrxt7xyf0h">
                <button type="button" className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-sand-300 text-[14px] font-semibold text-slate-700 transition-colors duration-150 ease-emphasis hover:border-green-600 hover:text-green-700" data-id="element-794" data-mp="2kn4x92ffmoqf">
                  <CalendarPlusIcon aria-hidden className="h-4 w-4" data-id="element-795" data-mp="38c7ei965mfke" />
                  Add to calendar
                </button>
                <button type="button" className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-sand-300 text-[14px] font-semibold text-slate-700 transition-colors duration-150 ease-emphasis hover:border-green-600 hover:text-green-700" data-id="element-796" data-mp="33exn4nz7fttx">
                  <Share2Icon aria-hidden className="h-4 w-4" data-id="element-797" data-mp="3md09sw4a5eok" />
                  Share
                </button>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {others.length > 0 && <section className="bg-white py-14 lg:py-20" aria-labelledby="other-events-heading" data-id="element-798" data-mp="2zgf8ikufyrxb">
          <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-799" data-mp="2lpadxx5xkptu">
            <h2 id="other-events-heading" className="font-display text-[26px] text-ink" data-id="element-800" data-mp="2pqc6xw9985nd">
              Other upcoming events
            </h2>
            <ul className="mt-8 grid gap-6 md:grid-cols-3" data-id="element-801" data-mp="3dgzufpvnl3v1">
              {others.map(item => <li key={item.slug} data-id="element-802" data-mp="3idylfzk0sup0">
                  <Link to={`/events/${item.slug}`} className="group flex h-full flex-col rounded-card border border-sand-200 p-5 transition-colors duration-200 ease-emphasis hover:border-green-200 hover:bg-sand-50" data-id="element-803" data-mp="3icx6vl3spq2j">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-green-700" data-id="element-804" data-mp="1syiduolqvh18">
                      {item.type}
                    </span>
                    <span className="mt-2 font-display text-[18px] leading-snug text-ink" data-id="element-805" data-mp="1phmgg1ikwche">
                      {item.title}
                    </span>
                    <span className="mt-3 text-[14px] text-slate-600" data-id="element-806" data-mp="1lin784ptdtk5">
                      {item.date} · {item.city}
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>}

      <CtaBand title="Bring five people with you." description="Campaign events are how undecided voters meet the plan. Share this one in your ward." primary={{
      label: 'Volunteer at events',
      to: '/join'
    }} secondary={{
      label: 'Campaign calendar',
      to: '/events'
    }} data-id="element-807" data-mp="16spo436qru1t" />
    </>;
}